import { Heading, SearchInput } from '@mm/components';
import { useDebounce } from '@mm/hooks';
import { selectContacts } from '@mm/state/selectors/contactsSelector';
import { selectAudienceSorted } from '@mm/state/selectors/messengerAdSelectors';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useInfiniteScroll } from '../../../../../common/hooks/use-infinite-scroll';
import styles from './AudienceListSidebar.module.css';
import { useFetchAudiences } from './use-fetch-audiences';

interface Props {
  className: string;
  selectedAudienceId: number | undefined;
  navigateToAudience: (audienceId: number | undefined) => void;
}
export const AudienceListSidebar = (props: Props) => {
  const { className, selectedAudienceId, navigateToAudience } = props;
  const [filter, setFilter] = useState<string>();
  const debouncedFilter = useDebounce(filter, 250);
  const { fetch, fetchMore, pagination, isFetching } = useFetchAudiences({
    skipBusy: true,
  });
  useEffect(() => {
    if (debouncedFilter !== undefined) {
      fetch(1, 25, debouncedFilter);
    }
  }, [debouncedFilter, fetch]);

  // Freshly fetch audiences when component is mounted.
  useEffect(() => {
    fetch(1, 25);
  }, [fetch]);

  const audiences = useSelector(selectAudienceSorted);
  const contacts = useSelector(selectContacts);
  const { scrollContainerRef } = useInfiniteScroll<HTMLDivElement>({
    fetchMore,
    fetchDisabled: pagination.isLastPage,
  });

  return (
    <div
      className={classNames(className, styles.container)}
      ref={scrollContainerRef}
    >
      <div className={styles.heading}>
        <Heading size={5}>Audiences</Heading>
        <div className={styles.headingInfo}>
          To create a new audience, apply filters, then click 'Save as an
          Audience'
        </div>
        <SearchInput
          css={{ container: styles.searchInput }}
          value={filter}
          onChange={e => setFilter(e.target.value)}
          placeholder="Audience Search"
        />
      </div>

      <div className={styles.audiences}>
        <div
          className={classNames(styles.audience, {
            [styles.audience__selected]: selectedAudienceId === undefined,
          })}
          onClick={() => navigateToAudience(undefined)}
        >
          <div className={styles.audienceName}>
            <strong>All Contacts</strong>
          </div>
          <div className={styles.audienceContactCountContainer}>
            <div className={styles.audienceContactCount}>
              {contacts.pagination.total}
            </div>
          </div>
        </div>

        {audiences.map(audience => (
          <div
            className={classNames(styles.audience, {
              [styles.audience__selected]: selectedAudienceId === audience.id,
            })}
            key={audience.id}
            onClick={() => navigateToAudience(audience.id)}
          >
            <div className={styles.audienceName}>{audience.name}</div>
            <div className={styles.audienceContactCountContainer}>
              <div className={styles.audienceContactCount}>
                {audience.contactsCount}
              </div>
            </div>
          </div>
        ))}
      </div>
      {isFetching && (
        <div className={styles.loading}>
          <i className="fas fa-spinner fa-spin" />
          <span>Loading audiences...</span>
        </div>
      )}
    </div>
  );
};
